// @ts-strict-ignore
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Highlight, HighlightType, Icon } from '@node-space/ui-components'
import { BeneficiaryDetails, BeneficiaryEntityType, BeneficiaryType } from 'types/beneficiaries'

interface BeneficiaryEntityTypeHighlightProps {
  beneficiary: BeneficiaryDetails
}

interface EntityTypeHighlightProps {
  icon: React.ReactNode
  text: string
  highlightType: HighlightType
}

export const BeneficiaryEntityTypeHighlight = ({
  beneficiary,
}: BeneficiaryEntityTypeHighlightProps) => {
  const { t } = useTranslation()
  const isBusiness = beneficiary?.entityType === BeneficiaryEntityType.BUSINESS
  const isIndividual = beneficiary?.entityType === BeneficiaryEntityType.INDIVIDUAL
  const isSelfOwnedCompany =
    beneficiary?.beneficiaryType === BeneficiaryType.FIRST_PARTY && isBusiness

  const highlightProps: EntityTypeHighlightProps = useMemo(() => {
    if (isSelfOwnedCompany) {
      return {
        text: t('contacts.myCompany'),
        icon: <Icon name="BuildingOffice" size="small" aria-label={t('contacts.myCompany')} />,
        highlightType: HighlightType.Feature,
      }
    }

    if (isBusiness) {
      return {
        text: t('contacts.company'),
        icon: <Icon name="BuildingOffice" size="small" aria-label={t('contacts.company')} />,
        highlightType: HighlightType.Neutral,
      }
    }

    if (isIndividual) {
      return {
        text: t('contacts.individual'),
        icon: <Icon name="UserCircle" size="small" aria-label={t('contacts.individual')} />,
        highlightType: HighlightType.Neutral,
      }
    }
  }, [isBusiness, isIndividual, isSelfOwnedCompany])

  return (
    highlightProps && (
      <Highlight
        iconElement={highlightProps?.icon}
        type={highlightProps?.highlightType}
        variant="emphasis"
      >
        {highlightProps?.text}
      </Highlight>
    )
  )
}
